import { useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'es-toolkit';
import { captureError } from 'helpers/captureError';

import { type ListingSuggestion, MIN_QUERY_LENGTH, searchListings } from './listingSearch';

/**
 * Debounced inventory lookup behind the hero combobox. Mirrors the legacy
 * `ListingTypeAheadInput`: nothing below three characters, five suggestions,
 * newest query wins. A failed lookup surfaces as `hasError` so the form can
 * tell an outage apart from an address with no matches.
 */
export const useListingSuggestions = () => {
  const [suggestions, setSuggestions] = useState<ListingSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // oxlint-disable-next-line react/preserve-manual-memoization -- react19-baseline: pre-existing, do not copy. Fix the compiler errors inside the callback first
  const runSearch = useMemo(
    () =>
      // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
      debounce(async (query: string) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        try {
          const results = await searchListings(query, controller.signal);
          if (!controller.signal.aborted) {
            setSuggestions(results);
            setIsLoading(false);
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            captureError(error, 'makeOfferListingSuggestions');
            setSuggestions([]);
            setHasError(true);
            setIsLoading(false);
          }
        }
      }, 200),
    [],
  );

  useEffect(() => {
    return () => {
      runSearch.cancel();
      abortRef.current?.abort();
    };
  }, [runSearch]);

  const onQueryChange = (query: string) => {
    setHasError(false);
    if (query.trim().length < MIN_QUERY_LENGTH) {
      runSearch.cancel();
      abortRef.current?.abort();
      setSuggestions([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    runSearch(query.trim());
  };

  return { suggestions, isLoading, hasError, onQueryChange };
};
